type JsonSchema = Record<string, any>;

/**
 * Extends an existing JSON schema by merging in a new schema object that
 * contains additional properties and/or required fields.
 *
 * @param existingSchema - The existing JSON schema to extend.
 * @param extendedSchema - The schema object that contains additional properties
 * and/or required fields to add to the existing schema.
 * @returns A new JSON schema that is the extension of the existing schema.
 */
export function extendJsonSchema(
  existingSchema: JsonSchema,
  extendedSchema: JsonSchema | JsonSchema[]
): JsonSchema {
  if (Array.isArray(extendedSchema)) {
    let existingSchemaAcc = existingSchema;
    for (let i = 0; i < extendedSchema.length; i++) {
      existingSchemaAcc = extendJsonSchema(
        existingSchemaAcc,
        extendedSchema[i]
      );
    }
    return existingSchemaAcc;
  }
  const properties = {
    ...existingSchema.properties,
    ...extendedSchema.properties
  };
  const required = Array.from(
    new Set([
      ...(existingSchema.required ?? []),
      ...(extendedSchema.required ?? [])
    ])
  );

  // Recursively update the required properties of any nested objects
  for (const key in properties) {
    const property = properties[key];
    if (property.type === "object") {
      property.required = property.required
        ? Array.from(
            new Set([
              ...property.required,
              ...(extendedSchema.properties?.[key]?.required ?? [])
            ])
          )
        : extendedSchema.properties?.[key]?.required;
    }
  }

  return {
    ...existingSchema,
    ...extendedSchema,
    properties,
    required
  };
}
